/** Split on hyphen / en-dash / em-dash ranges. */
const BED_RANGE_SPLIT = /\s*[-–—]\s*/;

/** Parse a bed-range endpoint to a numeric count (studio/std → 0). */
function parseBedEndpoint(raw: string): number | null {
  const lower = raw.trim().toLowerCase();
  if (lower === 'studio' || lower === 'std') return 0;
  const n = parseInt(lower, 10);
  return !isNaN(n) && n >= 0 ? n : null;
}

function formatBedCount(n: number): string {
  return n === 0 ? 'Studio' : String(n);
}

/** Join bed options: "Studio", "1 & 2", "Studio, 1 & 2". */
function joinBedOptions(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} & ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
}

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy that
 * lists every option — "Studio, 1 & 2 Bedroom" — instead of a dash range.
 */
export function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(BED_RANGE_SPLIT).map((p) => p.trim()).filter(Boolean);
  const nums = parts.map(parseBedEndpoint);

  let options: string[];
  if (parts.length >= 2 && nums.every((n): n is number => n !== null)) {
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    options = [];
    for (let i = min; i <= max; i++) {
      options.push(formatBedCount(i));
    }
  } else {
    options = parts.map((p) => {
      const lower = p.toLowerCase();
      if (lower === 'studio' || lower === 'std') return 'Studio';
      return p;
    });
    if (options.length > 1 && options.every((p) => p.toLowerCase() === options[0].toLowerCase())) {
      options = [options[0]];
    }
  }

  const label = joinBedOptions(options);
  const allStudios = options.every((p) => p === 'Studio');
  return allStudios ? label : `${label} Bedroom`;
}

/** Legacy dash-style label used by saved hero copy (for rewriting). */
function formatBedroomsLabelDashStyle(raw: string): string {
  const parts = raw
    .split(BED_RANGE_SPLIT)
    .map((p) => {
      const lower = p.trim().toLowerCase();
      if (lower === 'studio' || lower === 'std') return 'Studio';
      return p.trim();
    })
    .filter(Boolean);
  const deduped =
    parts.length > 1 && parts.every((p) => p.toLowerCase() === parts[0].toLowerCase())
      ? [parts[0]]
      : parts;
  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? label : `${label} Bedroom`;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type RewriteBedroomRangeOptions = {
  /**
   * When true (homepage Hero), prepend the listed beds label if the resolved
   * HTML has no bed phrase. Inner-page SEO lines omit this so policy/contact
   * copy is not prefixed with bedrooms.
   */
  prependIfMissing?: boolean;
};

/**
 * Turn raw/dash bedrange copy into the listed label inside resolved HTML.
 * Also tidies empty "in ," location fragments when city/state resolve empty.
 */
export function rewriteBedroomRangeDisplay(
  html: string,
  rawRange: string | null | undefined,
  options: RewriteBedroomRangeOptions = {},
): string {
  const { prependIfMissing = false } = options;
  const formatted = formatBedroomsLabel(rawRange);
  let out = html;

  out = out.replace(
    /\b(?:Studio|Std|\d+)(?:\s*[-–—]\s*(?:Studio|Std|\d+))+\s+Bedrooms?\b/gi,
    formatted,
  );

  if (rawRange && rawRange.trim()) {
    const dashStyle = formatBedroomsLabelDashStyle(rawRange);
    if (dashStyle && out.includes(dashStyle)) {
      out = out.split(dashStyle).join(formatted);
    }
    for (const variant of [rawRange, rawRange.trim()]) {
      if (!variant || !out.includes(variant)) continue;
      out = out.replace(
        new RegExp(`${escapeRegExp(variant)}(?:\\s+Bedrooms?)?`, 'gi'),
        formatted,
      );
    }
  }

  if (
    prependIfMissing &&
    formatted &&
    !new RegExp(escapeRegExp(formatted), 'i').test(out)
  ) {
    if (/^<p>/i.test(out)) {
      out = out.replace(/^<p>/i, `<p>${formatted} `);
    } else {
      out = `${formatted} ${out}`;
    }
  }

  if (formatted) {
    out = out.replace(
      new RegExp(`(${escapeRegExp(formatted)})(?:\\s+\\1)+`, 'gi'),
      '$1',
    );
  }

  out = out
    .replace(/\s+in\s+,\s*/gi, ' in ')
    .replace(/\s+in\s*(<\/p>|$)/gi, '$1')
    .replace(/,\s*(<\/p>|$)/gi, '$1');

  return out;
}
